/**
 * useDebouncedSearch — generic hook for typeahead/search inputs that
 * debounce an async search function.
 *
 * Replaces the duplicated pattern across CustomerSearch, ShipToSearch,
 * CreateOrderDialog, and other typeahead components.
 *
 * Usage:
 *   const { results, loading, error, searched } = useDebouncedSearch(
 *     query,
 *     (q) => portalService.searchCustomers(q, 10),
 *     { minLength: 2, debounceMs: 300 },
 *   );
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export interface DebouncedSearchOptions {
  /** Minimum query length before triggering a search. Default: 2. */
  minLength?: number;
  /** Debounce delay in milliseconds. Default: 300. */
  debounceMs?: number;
  /** If true, the hook is disabled and no search is performed. */
  disabled?: boolean;
}

export interface DebouncedSearchResult<T> {
  results: T[];
  loading: boolean;
  error: string | null;
  /** True once at least one search has completed. */
  searched: boolean;
  /** Manually clear results and reset searched state. */
  reset: () => void;
}

export function useDebouncedSearch<T>(
  query: string,
  searchFn: (q: string) => Promise<T[]>,
  options: DebouncedSearchOptions = {},
): DebouncedSearchResult<T> {
  const { minLength = 2, debounceMs = 300, disabled = false } = options;

  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;

  const reset = useCallback(() => {
    setResults([]);
    setSearched(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (disabled || query.trim().length < minLength) {
      setResults([]);
      setSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await searchFnRef.current(query);
        setResults(data);
        setSearched(true);
      } catch {
        setError('Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, minLength, debounceMs, disabled]);

  return { results, loading, error, searched, reset };
}
